import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Leaf, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";

const NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "Rooms", path: "/rooms" },
  { label: "Dining Area", path: "/dining" },
  { label: "Restaurant", path: "/restaurant" },
  { label: "Contact", path: "/contact" },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8fdea702-ef33-49ea-a556-0fc99f37063c/artifacts/zil2jqvm_TFE.creative.agency.png";

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-100" style={{ boxShadow: '0 1px 12px rgba(0,0,0,0.04)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img src={LOGO_URL} alt="Ambe Palace - Best hotel in Dungarpur Rajasthan logo" className="h-10 md:h-14 w-auto" />
            <div className="hidden sm:block">
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Ambe Palace
              </span>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider -mt-0.5">Hotel & Restaurant</p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_ITEMS.map(item => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 pb-1 ${
                  location.pathname === item.path
                    ? "text-amber-600 border-b-2 border-amber-600"
                    : "text-slate-600 hover:text-amber-600"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-1.5 border border-green-600 rounded-full px-2.5 py-1" data-testid="pure-veg-badge">
              <Leaf className="w-3 h-3 text-green-600" />
              <span className="text-[10px] font-bold text-green-700 uppercase tracking-wider">Pure Veg</span>
            </div>

            <a href="tel:+916376696360" className="hidden md:flex items-center gap-1.5 text-slate-600 hover:text-amber-600 transition-colors duration-200">
              <Phone className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold">6376696360</span>
            </a>

            <Button
              onClick={() => navigate("/rooms")}
              className="bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold uppercase tracking-[0.15em] rounded-none px-4 md:px-6 py-2 h-9 shadow-sm hover:shadow-md transition-shadow duration-200"
              data-testid="header-book-now-btn"
            >
              Book Now
            </Button>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5 text-slate-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 pt-10">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 mb-6">
                    <img src={LOGO_URL} alt="Ambe Palace - Best hotel in Dungarpur Rajasthan" className="h-10" />
                    <div>
                      <p className="font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>Ambe Palace</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Leaf className="w-3 h-3 text-green-600" />
                        <span className="text-[10px] font-bold text-green-700">Pure Veg</span>
                      </div>
                    </div>
                  </div>
                  {NAV_ITEMS.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`text-sm font-bold uppercase tracking-wider py-3 px-3 rounded-sm transition-colors duration-200 ${
                        location.pathname === item.path
                          ? "text-amber-600 bg-amber-50"
                          : "text-slate-700 hover:bg-stone-50"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <div className="border-t border-stone-100 my-4" />
                  <Button
                    onClick={() => { navigate("/rooms"); setOpen(false); }}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider rounded-none"
                    data-testid="mobile-book-now-btn"
                  >
                    Book Now
                  </Button>
                  <a href="tel:+916376696360" className="flex items-center gap-2 text-slate-600 mt-4 px-3">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm font-semibold">6376696360</span>
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
